import React, {useEffect, useRef} from "react";
import PropTypes from "prop-types";
import SunEditor from 'suneditor-react';
import {errorMessage, getValue, isInvalid, onBlur, onChange} from "./Component";

const RichText = (props) => {
  function getClassName(props) {
    return "rich-text loadable"
      + (isInvalid(props) ? " is-invalid" : "")
      + (props.className ?? "")
      + (props.label ? " has-float-label" : "")
      + (props.disabled ? " is-disabled" : "");
  }

  function getSunEditorInstance(sunEditor) {
    editor.current = sunEditor;
  }

  const editor = useRef({core: {}});

  useEffect(() => {

    editor.current.core.history?.reset(true);
  }, [props.id]);

  return (
    <div className={getClassName(props)}>
      <SunEditor
        lang={sl}
        onChange={value => onChange(props, value)}
        disable={props.disabled}
        onBlur={() => onBlur(props)}
        onPaste={() => onPaste(props)}
        onDrop={() => onDrop(props)}
        setContents={getValue(props)}
        setOptions={{
          height: 400,
          buttonList: props.buttonList ?? [
            ['undo', 'redo'],
            ['font', 'fontSize', 'formatBlock'],
            ['paragraphStyle', 'blockquote'],
            ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
            ['fontColor', 'hiliteColor'],
            ['removeFormat'],
            ['outdent', 'indent'],
            ['align', 'horizontalRule', 'list', 'lineHeight'],
            !['table', 'link', 'image',],
            ['showBlocks', 'codeView']
          ]
        }}
        getSunEditorInstance={getSunEditorInstance}/>
      <span>{props.label}</span>
      <div className="error-icon fz fz-exclamation-triangle solid">
        <div className="has-tooltip danger" data-tooltip={errorMessage(props)}/>
      </div>
    </div>
  );
}

RichText.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), //change to reset history
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  formField: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  buttonList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))
};


export default RichText

const sl = {
  code: 'si',
  toolbar: {
    default: 'Privzeto',
    save: 'Shrani',
    font: 'Pisava',
    formats: 'Oblike zapisa',
    fontSize: 'Velikost',
    bold: 'Odebeljeno',
    underline: 'Podčrtano',
    italic: 'Poševno',
    strike: 'Prečrtano',
    subscript: 'Nadpisano',
    superscript: 'Podpisano',
    removeFormat: 'Odstrani obliko zapisa',
    fontColor: 'Barva pisave',
    hiliteColor: 'Barva ozadja',
    indent: 'Povečja zamik',
    outdent: 'Zmanjšaj zamik',
    align: 'Poravnava',
    alignLeft: 'Levo',
    alignRight: 'Desno',
    alignCenter: 'Sredina',
    alignJustify: 'Obojestransko',
    list: 'Seznam',
    orderList: 'Oštevilčen seznam',
    unorderList: 'Označeni sezname',
    horizontalRule: 'Vodoravna črta',
    hr_solid: 'polno',
    hr_dotted: 'pikasto',
    hr_dashed: 'črtasto',
    table: 'Tabela',
    link: 'Povezava',
    image: 'Slika',
    video: 'Video',
    fullScreen: 'Celotni zaslon',
    showBlocks: 'Pregled blokov',
    codeView: 'Izvorna koda',
    undo: 'Razveljavi',
    redo: 'Uveljavi',
    preview: 'Predogled',
    print: 'natisni',
    tag_p: 'Odstavek',
    tag_div: 'Navadni (DIV)',
    tag_h: 'Naslov',
    tag_blockquote: 'Narekovaj',
    tag_pre: 'Koda',
    template: 'Predloga'
  },
  dialogBox: {
    linkBox: {
      title: 'Vstavi povezavo',
      url: 'naslov povezave (URL)',
      text: 'Prikazano besedilo',
      newWindowCheck: 'Odpri v novem oknu',
      downloadLinkCheck: 'Prenesi povezavo',
      bookmark: 'Zaznamek'
    },
    mathBox: {
      title: 'Matematika',
      inputLabel: 'Matematični zapis',
      fontSizeLabel: 'Velikost pisave',
      previewLabel: 'Predogled'
    },
    imageBox: {
      title: 'Vstavi sliko',
      file: 'Izberi datoteko',
      url: 'Naslov slike (URL)',
      altText: 'Privzeto besedilo'
    },
    videoBox: {
      title: 'Vstavi video',
      file: 'Izberi datoteko',
      url: 'Povezava do videa (URL), YouTube/Vimeo'
    },
    audioBox: {
      title: 'Vstavi zvočni zapis',
      file: 'Izberi datoteko',
      url: 'Naslov zvočne datoteke (URL)'
    },
    browser: {
      tags: 'Označbe',
      search: 'Iskanje',
    },
    caption: 'Vstavi opis',
    close: 'Zapri',
    submitButton: 'Pošlji',
    revertButton: 'Razveljvavi',
    proportion: 'Proporcije',
    basic: 'Osnovno',
    left: 'Levo',
    right: 'Desno',
    center: 'Na sredini',
    width: 'Širina',
    height: 'Višina',
    size: 'Velikost',
    ratio: 'Razmerje'
  },
  controller: {
    edit: 'Urejaj',
    unlink: 'Odstrani povezavo',
    remove: 'Odstrani',
    insertRowAbove: 'Vstavi vrstico zgoraj',
    insertRowBelow: 'Vstavi vrstico spodaj',
    deleteRow: 'Izbriši vrstico',
    insertColumnBefore: 'Vstavi stolpec pred',
    insertColumnAfter: 'Vstavi stolpec za',
    deleteColumn: 'Izbriši stolpec',
    fixedColumnWidth: 'Fiksna širina stolpca',
    resize100: 'Spremeni na 100%',
    resize75: 'Spremeni na 75%',
    resize50: 'Spremeni na 50%',
    resize25: 'Spremeni na 25%',
    autoSize: 'Avtomatska velikost',
    mirrorHorizontal: 'Preslikaj vodoravno',
    mirrorVertical: 'Preslikaj navpičnpo',
    rotateLeft: 'Zavrti lefo',
    rotateRight: 'Zavrti desno',
    maxSize: 'Največja velikost',
    minSize: 'Najmanjša velikost',
    tableHeader: 'Glava tabele',
    mergeCells: 'Spoji celice',
    splitCells: 'Razcepi celice',
    HorizontalSplit: 'Vodoravno razcepi',
    VerticalSplit: 'Navpično razcepi'
  },
  menu: {
    spaced: 'Razmaknjeno',
    bordered: 'Obrobljeno',
    neon: 'Neon',
    translucent: 'Prosojno',
    shadow: 'Senca',
    code: 'Koda'
  }
}
